/**
 * Audit log lines for threads (Discord's 110-112, 2026-10-29). The thread's
 * name comes from the store, else what the entry stored
 * (`options.nx_thread_name`, the `name` change), so a deleted thread is
 * still named.
 */
import { AuditLogEvent, type AuditLogEntry } from '@neosocial/protocol';
import type { Translator } from '../i18n';

function changeOf(e: AuditLogEntry, key: string) {
  return e.changes?.find((c) => c.key === key);
}

/** '#name', isolated left to right so the mark stays before the name. */
const hashed = (name: string) => '⁦#' + name + '⁩';

export function threadAuditLine(
  tr: Translator,
  e: AuditLogEntry,
  o: { actor: string; storeName?: string | null; channel: string },
): string {
  const { t } = tr;
  const named = changeOf(e, 'name');
  const name =
    o.storeName ||
    (named?.new_value as string | undefined) ||
    e.options?.nx_thread_name ||
    (named?.old_value as string | undefined) ||
    '—';
  const thread = hashed(name);
  const actor = o.actor;
  switch (e.action_type as number) {
    case AuditLogEvent.THREAD_CREATE:
      return t('th.audit.create', { actor, thread, channel: o.channel });
    case AuditLogEvent.THREAD_DELETE:
      return t('th.audit.delete', { actor, thread });
    default: {
      const locked = changeOf(e, 'locked');
      const archived = changeOf(e, 'archived');
      if (locked && (e.changes?.length ?? 0) <= 2)
        return t(locked.new_value ? 'th.audit.lock' : 'th.audit.unlock', { actor, thread });
      if (archived && e.changes?.length === 1)
        return t(archived.new_value ? 'th.audit.archive' : 'th.audit.unarchive', {
          actor,
          thread,
        });
      if (named && e.changes?.length === 1 && typeof named.old_value === 'string')
        return t('th.audit.rename', { actor, thread, from: hashed(named.old_value) });
      return t('th.audit.update', { actor, thread });
    }
  }
}
